/**
 * Entity System Service — thin re-export layer over orgYDocStore.
 *
 * All public function names are preserved so existing callers (entityStore.ts,
 * entityActions.ts, offlineEntityQueue.ts, etc.) continue to work without renaming.
 *
 * Real-time entity tree updates are delivered via the org Y.Doc room
 * (org:{dataspaceId}) which syncs through the WebSocket server.
 */

import type { BaseEntityType } from '../types'
import {
  initOrgYDocStore,
  destroyOrgYDocStore,
  waitForOrgSync,
  subscribeToEntitySystemChanges as _subscribeToEntitySystemChanges,
  getOrgYDocDebugState,
} from './orgYDocStore'

// Re-export debug state with the shape callers expect
export function getEntitySystemDebugState() {
  const org = getOrgYDocDebugState()
  return {
    dataspaceId: org.dataspaceId,
    indexeddbSynced: true,
    wsConnected: org.wsConnected,
    wsSynced: org.wsSynced,
    entityCount: org.nodeCount,
    edgeCount: org.edgeCount,
    lastUpdateAt: org.lastUpdateAt,
    loadSource: org.wsSynced ? 'ws' : null,
  }
}

/**
 * Returns null — entity loading uses REST (entityStore.initialize() REST path).
 */
export function getEntitiesFromYDoc(): BaseEntityType[] | null {
  return null
}

/**
 * Returns null — edge loading now uses the org Y.Doc observer.
 */
export function getEntitySystemEdges(): any[] | null {
  return null
}

export function waitForEntitySystemReady(): Promise<void> {
  return waitForOrgSync(10000).catch(() => {
    console.warn('[EntitySystem] waitForEntitySystemReady: 10s safety net fired')
  })
}

export function subscribeToEntitySystemChanges(
  callback: (action: 'add' | 'update' | 'delete', entityId: string, data?: BaseEntityType) => void,
): () => void {
  return _subscribeToEntitySystemChanges(callback)
}

/**
 * Subscribe to content reload events for a specific entity.
 *
 * When the backend agent writes to an entity (e.g. sheet_write), the Python
 * backend pushes an update to the org Y.Doc room. This triggers the
 * orgYDocStore nodesMap observer which calls _entityListeners. We filter for
 * the specific entity and call fn(), which fetches fresh base64 from REST and
 * merges it into the caller's local Y.Doc for immediate re-render.
 *
 * This is the reliable fallback for when the doc WS room is cold (no active
 * browser connection) and the WS push-update from nodeContext.ts commit() is
 * a no-op.
 */
export function registerContentReloadListener(entityId: string, fn: () => void): () => void {
  // The org Y.Doc can emit two rapid 'update' events for the same entity write
  // (e.g. node upsert + metadata update). Debounce collapses them into one call.
  let timer: ReturnType<typeof setTimeout> | null = null
  const unsubscribe = subscribeToEntitySystemChanges((action, changedEntityId) => {
    if ((action === 'update' || action === 'add') && changedEntityId === entityId) {
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        fn()
      }, 200)
    }
  })
  return () => {
    // Cancel any pending debounce timer before removing the subscription so that
    // fn() is never called on an unmounted component.
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    unsubscribe()
  }
}

// Module-level cache to avoid re-importing on every call. Populated on first use.
// Dynamic import is required to avoid a circular dependency
// (entityStore imports entitySystemYjsStore during initialization).
let _entityStoreCache: ReturnType<typeof import('./entityStore')['default']['getState']> | null = null

function _getEntityStore() {
  if (_entityStoreCache) return _entityStoreCache
  // Return null on first call — the async import below will populate the cache.
  // Subsequent calls will be synchronous via the cache.
  return null
}

async function _warmEntityStoreCache(): Promise<void> {
  const { default: useEntityStore } = await import('./entityStore')
  _entityStoreCache = useEntityStore.getState()
}

export function writeEntityToYDoc(entityId: string, data: BaseEntityType): void {
  const store = _getEntityStore()
  if (store) {
    store._applyLocalUpsert(data, { source: 'crdt', log: false, syncCRDT: false })
    return
  }
  _warmEntityStoreCache().then(() => {
    _entityStoreCache!._applyLocalUpsert(data, { source: 'crdt', log: false, syncCRDT: false })
  }).catch(() => {})
}

export function reconcileOfflineEntity(
  tempId: string,
  _realId: string,
  realData: BaseEntityType,
): void {
  const store = _getEntityStore()
  if (store) {
    store._applyLocalDelete(tempId, { source: 'crdt', log: false, syncCRDT: false })
    store._applyLocalUpsert(realData, { source: 'crdt', log: false, syncCRDT: false })
    return
  }
  _warmEntityStoreCache().then(() => {
    _entityStoreCache!._applyLocalDelete(tempId, { source: 'crdt', log: false, syncCRDT: false })
    _entityStoreCache!._applyLocalUpsert(realData, { source: 'crdt', log: false, syncCRDT: false })
  }).catch(() => {})
}

export function deleteEntityFromYDoc(entityId: string): boolean {
  const store = _getEntityStore()
  if (store) {
    store._applyLocalDelete(entityId, { source: 'crdt', log: false, syncCRDT: false })
    return true
  }
  _warmEntityStoreCache().then(() => {
    _entityStoreCache!._applyLocalDelete(entityId, { source: 'crdt', log: false, syncCRDT: false })
  }).catch(() => {})
  return true
}

export function setEdgePosition(_edgeId: string, _pos: string): void {
  // Edge position changes flow through the org Y.Doc and _flushToBackend REST call.
}

export function initEntitySystemYjsStore(dataspaceId: string): void {
  if (!dataspaceId || dataspaceId === 'unknown') return
  initOrgYDocStore(dataspaceId)
}

export function destroyEntitySystemYjsStore(): void {
  destroyOrgYDocStore()
}
